import React, { useEffect, useContext, useState, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { BsClock, BsCheckCircle, BsCheck2, BsPatchCheckFill, BsBellFill } from "react-icons/bs";
import { FaClock, FaStethoscope, FaThumbsUp, FaComment, FaRetweet } from "react-icons/fa";
import { GoDeviceCameraVideo } from "react-icons/go";
import { FiChevronRight } from "react-icons/fi";
import {
  _imagesURL,
  ReadNotificationsApi,
  getNotificationsApi,
} from "../../constants/API";
import Avatar from "../../assets/images/icon/placeholder_Awatar.png";
import Place from "../../assets/images/place.png";
import { toast } from "react-toastify";
import {
  EVENT_DETAILS,
  MENTORSHIP,
  NEW_COURSE_DETAILS,
  POST_DETAILS,
  PROFILE,
  SESSION_PAGE_DETAILS,
  WEBINAR_PAGE_DETAILS,
} from "../../constants/RoutesUrl";
import { Language } from "../../App";
import ImageComponent from "../common/ImageComponent";
import { CountContext } from "../../constants/ProtectedRoute";
import { authorize } from "../../shared/authorize";
import { getSplashData } from "../../services/api";
import { useQuery } from "react-query";
import { GetNotificationBody } from "../common/GetNotificationBody";
import NotificationSkeleton from "./NotificationSkeletonWave";
import PurchasedUsersModal from "../course-details/PurchasedUsersModal";
import { useCustomToast } from "../common/CustomToast";
import "./Notification.css";
import {
  IoPersonOutline,
  IoPersonAddOutline,
  IoNewspaperOutline,
  IoVideocamOutline,
  IoFlaskOutline,
  IoBookOutline,
  IoCalendarOutline,
  IoPulseOutline,
  IoWalletOutline,
  IoNotificationsOutline,
  IoSchoolOutline,
  IoEyeOutline,
} from "react-icons/io5";
import {
  LuCalendarDays,
  LuStethoscope,
  LuFileText,
  LuVideo,
  LuActivity,
  LuGraduationCap,
  LuCalendar,
  LuFilter,
  LuBarChart2,
} from "react-icons/lu";

/* ── Soft-3D per-type accent config ── */
const NOTIF_TYPE_CONFIG = {
  social:             { color: "#00BADA", gradient: "linear-gradient(135deg, #38BDF8 0%, #00BADA 50%, #0284C7 100%)", light: "#E8FBFF", border: "#BCEEFA", shadow: "rgba(0, 186, 218, 0.32)" },
  webinar:            { color: "#EF4444", gradient: "linear-gradient(135deg, #FB7185 0%, #EF4444 50%, #DC2626 100%)", light: "#FEF2F2", border: "#FECACA", shadow: "rgba(239, 68, 68, 0.28)" },
  clinical:           { color: "#10B981", gradient: "linear-gradient(135deg, #34D399 0%, #10B981 50%, #059669 100%)", light: "#ECFDF5", border: "#A7F3D0", shadow: "rgba(16, 185, 129, 0.28)" },
  course:             { color: "#F59E0B", gradient: "linear-gradient(135deg, #FBBF24 0%, #F59E0B 50%, #D97706 100%)", light: "#FFFBEB", border: "#FDE68A", shadow: "rgba(245, 158, 11, 0.28)" },
  event:              { color: "#6366F1", gradient: "linear-gradient(135deg, #818CF8 0%, #6366F1 50%, #4F46E5 100%)", light: "#EEF2FF", border: "#C7D2FE", shadow: "rgba(99, 102, 241, 0.28)" },
  "clinical-session": { color: "#06B6D4", gradient: "linear-gradient(135deg, #22D3EE 0%, #06B6D4 50%, #0891B2 100%)", light: "#ECFEFF", border: "#CFFAFE", shadow: "rgba(6, 182, 212, 0.28)" },
  Wallet:             { color: "#10B981", gradient: "linear-gradient(135deg, #34D399 0%, #059669 100%)", light: "#ECFDF5", border: "#A7F3D0", shadow: "rgba(16, 185, 129, 0.28)" },
  mentor:             { color: "#7C5CFC", gradient: "linear-gradient(135deg, #A78BFA 0%, #7C5CFC 50%, #6D28D9 100%)", light: "#F5F3FF", border: "#DDD6FE", shadow: "rgba(124, 92, 252, 0.28)" },
};

const getNotifConfig = (type) => NOTIF_TYPE_CONFIG[type] || NOTIF_TYPE_CONFIG.social;

const Notification = () => {
  const { showToast } = useCustomToast();
  const { count } = useContext(CountContext);
  const savedNotificationType = sessionStorage.getItem("lastNotificationType");
  const returnFromNavigation = sessionStorage.getItem("returnFromNotificationNavigation");

  const { lang } = useContext(Language);
  const { counts = {} } = count ?? {};

  const getSectionCount = (val) => {
    const parsed = parseInt(val, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  };

  const btns = [
    {
      id: 1,
      type: "social",
      name: lang?.posts || "Posts",
      count: getSectionCount(counts?.social),
      icon: LuFileText,
      color: NOTIF_TYPE_CONFIG.social.color,
      iconBg: NOTIF_TYPE_CONFIG.social.light,
    },
    {
      id: 2,
      type: "webinar",
      name: lang?.webinars || "Webinars",
      count: getSectionCount(counts?.webinar),
      icon: LuVideo,
      color: NOTIF_TYPE_CONFIG.webinar.color,
      iconBg: NOTIF_TYPE_CONFIG.webinar.light,
    },
    {
      id: 3,
      type: "clinical",
      name: lang?.clinical_studies || "Clinical Studies",
      count: getSectionCount(counts?.clinical),
      icon: LuActivity,
      color: NOTIF_TYPE_CONFIG.clinical.color,
      iconBg: NOTIF_TYPE_CONFIG.clinical.light,
    },
    {
      id: 4,
      type: "course",
      name: lang?.courses || "Courses",
      count: getSectionCount(counts?.course),
      icon: LuGraduationCap,
      color: NOTIF_TYPE_CONFIG.course.color,
      iconBg: NOTIF_TYPE_CONFIG.course.light,
    },
    {
      id: 5,
      type: "event",
      name: lang?.events || "Events",
      count: getSectionCount(counts?.event),
      icon: LuCalendar,
      color: NOTIF_TYPE_CONFIG.event.color,
      iconBg: NOTIF_TYPE_CONFIG.event.light,
    },
    {
      id: 6,
      type: "clinical-session",
      name: lang?.clinical_sessions || "Clinical Sessions",
      count: getSectionCount(counts?.clinical_session),
      icon: IoPulseOutline,
      color: NOTIF_TYPE_CONFIG["clinical-session"].color,
      iconBg: NOTIF_TYPE_CONFIG["clinical-session"].light,
    },
    {
      id: 7,
      type: "Wallet",
      name: lang?.wallet || "Wallet",
      count: getSectionCount(counts?.wallet),
      icon: IoWalletOutline,
      color: NOTIF_TYPE_CONFIG.Wallet.color,
      iconBg: NOTIF_TYPE_CONFIG.Wallet.light,
    },
    {
      id: 8,
      type: "mentor",
      name: lang?.mentorship_consultation_tab || "Consultation / Mentorship",
      count: getSectionCount(counts?.mentor),
      icon: LuStethoscope,
      color: NOTIF_TYPE_CONFIG.mentor.color,
      iconBg: NOTIF_TYPE_CONFIG.mentor.light,
    },
  ];

  // Calculate total unread notifications count by summing each section's unread count
  const totalUnread = btns.reduce((acc, btn) => acc + (btn.count || 0), 0);

  // Function to get the active button based on notification type
  const getActiveBtnFromType = (type) => {
    const btn = btns.find((b) => b.type === type);
    return btn ? btn.id : 1;
  };

  // Initialize state with proper values immediately
  const getInitialState = () => {
    if (returnFromNavigation === "true" && savedNotificationType) {
      return {
        activeBtn: getActiveBtnFromType(savedNotificationType),
        notificationsType: savedNotificationType,
      };
    } else {
      return {
        activeBtn: 1,
        notificationsType: "social",
      };
    }
  };

  const initialState = getInitialState();

  // States
  const [dataLoading, setDataLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [activeBtn, setActiveBtn] = useState(initialState.activeBtn);
  const [notificationsType, setNotificationsType] = useState(initialState.notificationsType);
  const [notificationData, setNotificationData] = useState({});
  const [isInitialized, setIsInitialized] = useState(false);
  const { refetch } = useQuery("splashData", getSplashData);
  const scrollSentinelRef = useRef(null);
  const hasMorePages = page < lastPage;

  // Sidebar indicator tracking matching SearchSidebarFilter
  const itemRefs = useRef({});
  const [indicatorStyle, setIndicatorStyle] = useState({ top: 0, height: 0 });

  useEffect(() => {
    const timer = setTimeout(() => {
      const activeItem = itemRefs.current[notificationsType];
      if (activeItem) {
        setIndicatorStyle({
          top: activeItem.offsetTop,
          height: activeItem.offsetHeight,
        });
      }
    }, 0);
    return () => clearTimeout(timer);
  }, [notificationsType, isInitialized]);

  // Handle sessionStorage cleanup and initialization
  useEffect(() => {
    if (returnFromNavigation === "true" && savedNotificationType) {
      const restoredActiveBtn = getActiveBtnFromType(savedNotificationType);
      setNotificationsType(savedNotificationType);
      setActiveBtn(restoredActiveBtn);
      sessionStorage.removeItem("returnFromNotificationNavigation");
    } else {
      setNotificationsType("social");
      setActiveBtn(1);
      sessionStorage.removeItem("lastNotificationType");
    }
    setIsInitialized(true);
  }, []);

  const handleReadNotification = (typeToRead = notificationsType) => {
    axios
      .post(ReadNotificationsApi, { type: typeToRead }, authorize())
      .then(() => {
        refetch();
      })
      .catch((err) => {
        console.warn("Background mark read failed:", err);
      });
  };

  const handleManualMarkAllAsRead = (typeToRead = notificationsType) => {
    const currentCount = activeCategoryObj?.count || 0;
    if (!currentCount || currentCount <= 0) {
      return;
    }

    axios
      .post(ReadNotificationsApi, { type: typeToRead }, authorize())
      .then(() => {
        refetch();
        showToast(
          lang?.all_notifications_marked_as_read ||
            "All notifications marked as read",
          "success"
        );
      })
      .catch((err) => {
        showToast(
          err?.message ||
            lang?.notifications_mark_read_failed ||
            "Failed to mark notifications as read",
          "error"
        );
      });
  };

  const handleLoadMore = () => {
    if (loadingMore || dataLoading || !hasMorePages) return;
    const nextPage = page + 1;
    setLoadingMore(true);
    const currentType = notificationsType;

    axios
      .post(getNotificationsApi, { type: currentType, page: nextPage }, authorize())
      .then((res) => {
        if (currentType !== notificationsType) return;
        if (res.data.status) {
          const incoming = res.data.data;
          setNotificationData((prev) => ({
            ...incoming,
            data: [...(prev?.data || []), ...(incoming?.data || [])],
          }));
          setPage(incoming?.current_page || nextPage);
          setLastPage(incoming?.last_page || nextPage);
        } else {
          toast.error(res.data.action);
        }
        setLoadingMore(false);
      })
      .catch((err) => {
        setLoadingMore(false);
        toast.error(err);
      });
  };

  useEffect(() => {
    if (!isInitialized) return;

    let isCancelled = false;
    setDataLoading(true);
    setNotificationData({});
    setPage(1);
    setLastPage(1);

    axios
      .post(getNotificationsApi, { type: notificationsType, page: 1 }, authorize())
      .then((res) => {
        if (isCancelled) return;
        if (res.data.status) {
          const incoming = res.data.data;
          setNotificationData(incoming || {});
          setPage(incoming?.current_page || 1);
          setLastPage(incoming?.last_page || 1);
          handleReadNotification(notificationsType);
        } else {
          toast.error(res.data.action);
        }
        setDataLoading(false);
      })
      .catch((err) => {
        if (isCancelled) return;
        setDataLoading(false);
        toast.error(err);
      });

    return () => {
      isCancelled = true;
    };
  }, [notificationsType, isInitialized]);

  // Infinite scroll
  useEffect(() => {
    const node = scrollSentinelRef.current;
    if (!node || !hasMorePages || dataLoading || loadingMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loadingMore && !dataLoading) {
          handleLoadMore();
        }
      },
      { rootMargin: "200px" }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [page, lastPage, loadingMore, dataLoading, notificationsType, hasMorePages]);

  const handleTabChange = (btnId, type) => {
    if (type === notificationsType) return;
    setActiveBtn(btnId);
    setNotificationsType(type);
    setNotificationData({});
    setDataLoading(true);
    setPage(1);
    setLastPage(1);
    sessionStorage.setItem("lastNotificationType", type);
  };

  // Group notifications by date for structured layout
  const groupedNotifications = [];
  if (notificationData?.data && Array.isArray(notificationData.data) && notificationData.data.length > 0) {
    let currentDate = null;
    let currentGroup = null;

    notificationData.data.forEach((item) => {
      const itemDate = item.date || "Recent";
      if (itemDate !== currentDate) {
        currentDate = itemDate;
        currentGroup = {
          date: itemDate,
          items: [item],
        };
        groupedNotifications.push(currentGroup);
      } else {
        currentGroup.items.push(item);
      }
    });
  }

  const activeCategoryObj = btns.find((b) => b.type === notificationsType) || btns[0];
  const ActiveCatIcon = activeCategoryObj.icon;
  const activeConfig = getNotifConfig(notificationsType);

  if (!isInitialized) {
    return (
      <div className="notif-page-wrapper">
        <div className="notif-workspace-container">
          <div className="notif-workspace-grid">
            <div className="notif-sidebar-rail">
              <div className="psf-card p-3">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="skeleton-wave" style={{ height: "36px", borderRadius: "8px", marginBottom: "6px" }} />
                ))}
              </div>
            </div>
            <div className="notif-feed-column">
              <div className="notif-group-surface">
                {Array.from({ length: 6 }).map((_, index) => (
                  <NotificationSkeleton key={index} notificationsType="social" />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="notif-page-wrapper">
      <div className="notif-workspace-container">
        {/* ── Page Header (Editorial & Soft-3D) ── */}
        <div className="notif-page-header">
          <div className="notif-header-content">
            <div className="notif-header-3d-box" title="Notification Center">
              <BsBellFill size={20} className="notif-bell-3d-icon" />
              {totalUnread > 0 && <span className="notif-header-beacon-dot" />}
            </div>
            <div className="notif-page-header-text">
              <div className="notif-title-badge-row">
                <h1 className="notif-main-title">{lang?.notifications || "Notifications"}</h1>
                {totalUnread > 0 && (
                  <span className="notif-total-badge" title="Unread notifications">
                    {totalUnread} {lang?.unread || "unread"}
                  </span>
                )}
              </div>
              <p className="notif-main-subtitle">
                {lang?.notifications_header_subtitle || "Stay updated with activity across your professional network."}
              </p>
            </div>
          </div>
        </div>

        {/* ── Mobile & Tablet Category Scroll Bar ── */}
        <div className="notif-mobile-tabs-bar">
          {btns.map((btn) => {
            const Icon = btn.icon;
            const isActive = activeBtn === btn.id;
            return (
              <button
                key={btn.id}
                type="button"
                onClick={() => handleTabChange(btn.id, btn.type)}
                className={`notif-mobile-tab-pill ${isActive ? "active" : ""}`}
              >
                <Icon size={15} />
                <span>{btn.name}</span>
                {Boolean(btn.count && btn.count !== 0) && (
                  <span className="notif-mobile-badge">{btn.count}</span>
                )}
              </button>
            );
          })}
        </div>

        {/* ── Two-Column Workspace Layout ── */}
        <div className="notif-workspace-grid">
          {/* Left Category Rail */}
          <aside className="notif-sidebar-rail">
            <div style={{ position: "relative", width: "100%" }}>
              {/* ── Ambient glow ── */}
              <div
                style={{
                  position: "absolute",
                  top: "-20px",
                  right: "-20px",
                  width: "90px",
                  height: "90px",
                  background: activeConfig.color,
                  opacity: 0.08,
                  filter: "blur(35px)",
                  borderRadius: "50%",
                  pointerEvents: "none",
                  zIndex: 0,
                  transition: "background 0.3s ease",
                }}
              />
              <div className="psf-card">
                {/* Header */}
                <div className="psf-header">
                  <div className="d-flex align-items-center gap-2">
                    <div className="psf-header-3d-icon">
                      <LuFilter size={14} />
                    </div>
                    <h4 className="psf-header-title">
                      {lang?.categories || "Categories"}
                    </h4>
                  </div>

                  {/* Total Count Badge */}
                  {Boolean(totalUnread && totalUnread > 0) && (
                    <span className="psf-header-total-badge">
                      {totalUnread}
                    </span>
                  )}
                </div>

                {/* Filter list */}
                <ul className="list-unstyled mb-0" style={{ position: "relative" }}>
                  {/* Animated left indicator bar */}
                  <div
                    className="psf-indicator"
                    style={{
                      height: `${indicatorStyle.height}px`,
                      transform: `translateY(${indicatorStyle.top}px)`,
                      background: activeConfig.color,
                    }}
                  />

                  {btns.map((item) => {
                    const isActive = notificationsType === item.type;
                    const cfg = getNotifConfig(item.type);
                    const IconEl = item.icon;
                    const count = item.count;

                    return (
                      <li key={item.id}>
                        <button
                          ref={(el) => (itemRefs.current[item.type] = el)}
                          onClick={() => handleTabChange(item.id, item.type)}
                          onKeyDown={(e) => e.key === "Enter" && handleTabChange(item.id, item.type)}
                          className={`psf-item ${isActive ? "is-active" : ""}`}
                          style={{
                            background: isActive ? "linear-gradient(90deg, #F0FDFA 0%, #FFFFFF 100%)" : "transparent",
                          }}
                          role="button"
                          tabIndex={0}
                          aria-label={`Filter by ${item.name}`}
                          aria-current={isActive ? "true" : undefined}
                        >
                          {/* 3D Contextual Icon badge */}
                          <div
                            className={`psf-icon-wrap ${isActive ? "active-3d" : ""}`}
                            style={{
                              background: isActive ? cfg.gradient : "#F8FAFC",
                              color: isActive ? "#FFFFFF" : cfg.color,
                              boxShadow: isActive
                                ? `0 3px 8px ${cfg.shadow}, inset 0 1px 1px rgba(255, 255, 255, 0.45)`
                                : "inset 0 1px 1px rgba(255, 255, 255, 0.9), 0 1px 2px rgba(15, 23, 42, 0.04)",
                              border: `1px solid ${isActive ? "transparent" : "#E2E8F0"}`,
                            }}
                          >
                            <IconEl size={16} />
                          </div>

                          {/* Label */}
                          <span
                            className="psf-label"
                            style={{
                              color: isActive ? "#0F172A" : "#475569",
                              fontWeight: isActive ? 700 : 500,
                            }}
                          >
                            {item.name}
                          </span>

                          {/* Count badge or active dot */}
                          {Boolean(count && count !== 0) ? (
                            <span
                              className="psf-count-badge"
                              style={{
                                color: isActive ? "#FFFFFF" : cfg.color,
                                backgroundColor: isActive ? cfg.color : cfg.light,
                                border: `1px solid ${isActive ? cfg.color : cfg.border}`,
                                boxShadow: isActive ? `0 2px 6px ${cfg.shadow}` : "none",
                              }}
                            >
                              {count}
                            </span>
                          ) : (
                            isActive && (
                              <div
                                className="psf-active-dot"
                                style={{
                                  background: cfg.color,
                                  boxShadow: `0 0 8px ${cfg.color}`,
                                }}
                              />
                            )
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>

            {/* Sidebar Footer Hint */}
            <div className="notif-sidebar-footer-hint">
              <div className="notif-hint-icon">
                <IoNotificationsOutline size={16} />
              </div>
              <div className="notif-hint-text">
                <span className="notif-hint-title">{lang?.stay_informed || "Stay informed"}</span>
                <span className="notif-hint-desc">
                  {lang?.stay_informed_desc || "Alerts from your network & activity"}
                </span>
              </div>
            </div>
          </aside>

          {/* Right Main Notification Feed */}
          <main className="notif-feed-column">
            {/* Integrated Section Header */}
            <div className="notif-section-header">
              <div className="notif-section-header-left">
                <div className="d-flex align-items-center gap-2">
                  <div
                    className="notif-section-icon-badge"
                    style={{
                      background: activeConfig.gradient,
                      boxShadow: `0 3px 10px ${activeConfig.shadow}, inset 0 1px 1px rgba(255, 255, 255, 0.5)`,
                    }}
                  >
                    <ActiveCatIcon size={17} color="#FFFFFF" />
                  </div>
                  <div>
                    <h2 className="notif-section-title">
                      {activeCategoryObj.name}
                    </h2>
                    <p className="notif-section-subtitle">
                      {activeCategoryObj.count
                        ? `${activeCategoryObj.count} new notifications`
                        : "Recent activity updates"}
                    </p>
                  </div>
                </div>
              </div>

              {Boolean(activeCategoryObj?.count && activeCategoryObj.count > 0) && (
                <button
                  type="button"
                  className="notif-mark-read-action"
                  onClick={() => handleManualMarkAllAsRead(notificationsType)}
                  title={lang?.mark_all_read || "Mark all as read"}
                >
                  <BsCheck2 size={15} />
                  <span>{lang?.mark_all_read || "Mark all as read"}</span>
                </button>
              )}
            </div>

            {/* Feed Activity List */}
            {dataLoading ? (
              <div className="notif-group-surface">
                {Array.from({ length: 7 }).map((_, index) =>
                  notificationsType === "mentor" ? (
                    <NotificationCardMentorSkeleton key={index} />
                  ) : (
                    <NotificationSkeleton
                      key={index}
                      notificationsType={notificationsType}
                    />
                  )
                )}
              </div>
            ) : (!notificationData?.data || notificationData.data.length === 0) ? (
              <NotificationEmptyState
                notificationsType={notificationsType}
                typeName={activeCategoryObj.name}
              />
            ) : (
              <>
                {groupedNotifications.map((group, groupIdx) => (
                  <div key={groupIdx} className="notif-date-group-wrap">
                    <div className="notif-date-divider-row">
                      <span className="notif-date-label">{group.date}</span>
                      <div className="notif-date-divider-line" />
                    </div>

                    <div className="notif-group-surface">
                      {group.items.map((value) => (
                        <React.Fragment key={value.id}>
                          {value.data.type === "mentor" ? (
                            <NotificationCardMentor
                              value={value}
                              currentNotificationType={notificationsType}
                            />
                          ) : value.data.notification_type === "follow" ? (
                            <NotificationCardFollow
                              value={value}
                              currentNotificationType={notificationsType}
                            />
                          ) : (
                            <NotificationCardLike
                              value={value}
                              currentNotificationType={notificationsType}
                            />
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                ))}

                {loadingMore && (
                  <div className="notif-group-surface" style={{ marginTop: "14px" }}>
                    {Array.from({ length: 3 }).map((_, index) =>
                      notificationsType === "mentor" ? (
                        <NotificationCardMentorSkeleton key={`more-${index}`} />
                      ) : (
                        <NotificationSkeleton
                          key={`more-${index}`}
                          notificationsType={notificationsType}
                        />
                      )
                    )}
                  </div>
                )}

                {hasMorePages && <div ref={scrollSentinelRef} style={{ height: "1px" }} />}
              </>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default Notification;

// Utility to render action badge icon overlay at bottom-right of avatar
const renderActionBadge = (type, notificationType) => {
  const notifTypeStr = (notificationType || type || "").toLowerCase();

  if (notifTypeStr.includes("follow")) {
    return (
      <span className="notif-action-badge" style={{ background: "#0D9488" }}>
        <IoPersonAddOutline size={10} />
      </span>
    );
  }
  if (notifTypeStr.includes("comment") || notifTypeStr.includes("reply")) {
    return (
      <span className="notif-action-badge" style={{ background: "linear-gradient(135deg, #A78BFA 0%, #7C3AED 100%)" }} title="Comment">
        <FaComment size={9} />
      </span>
    );
  }
  if (notifTypeStr.includes("like") || notifTypeStr.includes("reaction")) {
    return (
      <span className="notif-action-badge" style={{ background: "linear-gradient(135deg, #38BDF8 0%, #00BADA 50%, #0284C7 100%)" }} title="Like">
        <FaThumbsUp size={10} />
      </span>
    );
  }
  if (notifTypeStr.includes("repost") || notifTypeStr.includes("share")) {
    return (
      <span className="notif-action-badge" style={{ background: "linear-gradient(135deg, #34D399 0%, #10B981 100%)" }} title="Repost">
        <FaRetweet size={10} />
      </span>
    );
  }
  if (type === "course" || notifTypeStr.includes("course") || notifTypeStr.includes("purchase")) {
    return (
      <span className="notif-action-badge" style={{ background: "linear-gradient(135deg, #FBBF24 0%, #F59E0B 100%)" }} title="Course">
        <IoBookOutline size={10} />
      </span>
    );
  }
  if (type === "event" || notifTypeStr.includes("event") || notifTypeStr.includes("join")) {
    return (
      <span className="notif-action-badge" style={{ background: "linear-gradient(135deg, #818CF8 0%, #6366F1 100%)" }} title="Event">
        <IoCalendarOutline size={10} />
      </span>
    );
  }
  if (type === "webinar" || notifTypeStr.includes("webinar") || notifTypeStr.includes("reserve") || notifTypeStr.includes("host")) {
    return (
      <span className="notif-action-badge" style={{ background: "linear-gradient(135deg, #FB7185 0%, #EF4444 100%)" }} title="Webinar">
        <IoVideocamOutline size={10} />
      </span>
    );
  }
  if (type === "clinical-session" || notifTypeStr.includes("clinical") || notifTypeStr.includes("invite")) {
    return (
      <span className="notif-action-badge" style={{ background: "linear-gradient(135deg, #22D3EE 0%, #06B6D4 100%)" }} title="Clinical Session">
        <IoPulseOutline size={10} />
      </span>
    );
  }
  if (type === "Wallet" || notifTypeStr.includes("wallet") || notifTypeStr.includes("payment") || notifTypeStr.includes("refund")) {
    return (
      <span className="notif-action-badge" style={{ background: "linear-gradient(135deg, #34D399 0%, #059669 100%)" }} title="Wallet">
        <IoWalletOutline size={10} />
      </span>
    );
  }
  if (type === "mentor" || notifTypeStr.includes("mentor")) {
    return (
      <span className="notif-action-badge" style={{ background: "linear-gradient(135deg, #A78BFA 0%, #7C5CFC 100%)" }} title="Mentorship">
        <LuGraduationCap size={10} />
      </span>
    );
  }
  return (
    <span className="notif-action-badge" style={{ background: "linear-gradient(135deg, #38BDF8 0%, #00BADA 100%)" }}>
      <IoNotificationsOutline size={10} />
    </span>
  );
};

const capitalizeWords = (str) => {
  if (!str) return str;
  return str
    .split(" ")
    .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : word))
    .join(" ");
};

const EMPTY_STATE_ICONS = {
  social: IoNewspaperOutline,
  webinar: IoVideocamOutline,
  clinical: IoFlaskOutline,
  course: IoBookOutline,
  event: IoCalendarOutline,
  "clinical-session": IoPulseOutline,
  Wallet: IoWalletOutline,
  mentor: FaStethoscope,
};

const NotificationEmptyState = ({ notificationsType, typeName }) => {
  const { lang } = useContext(Language);
  const cfg = getNotifConfig(notificationsType);
  const EmptyIcon = EMPTY_STATE_ICONS[notificationsType] || IoNotificationsOutline;
  const subtitleTemplate =
    lang?.notifications_empty_subtitle ||
    "New {{type}} notifications will show up here as they happen.";

  return (
    <div className="notif-empty-container">
      <div
        className="notif-empty-3d-circle"
        style={{
          background: `radial-gradient(circle at 35% 35%, #FFFFFF 0%, ${cfg.light} 65%, ${cfg.border} 100%)`,
          borderColor: cfg.border,
          boxShadow: `0 8px 24px ${cfg.shadow}, inset 0 2px 4px rgba(255, 255, 255, 0.9)`,
          color: cfg.color,
        }}
      >
        <EmptyIcon size={34} />
      </div>
      <h3 className="notif-empty-heading">
        {lang?.notifications_empty_title || "No notifications yet"}
      </h3>
      <p className="notif-empty-description">
        {subtitleTemplate.replace("{{type}}", (typeName || "").toLowerCase())}
      </p>
    </div>
  );
};

export function UnixToRelative({ unixTimestamp }) {
  const currentTimestamp = Math.floor(Date.now() / 1000);
  const timestampDifference = currentTimestamp - unixTimestamp;
  const { lang } = useContext(Language);

  const intervals = [
    { singular: lang?.year, plural: lang?.years, seconds: 31536000 },
    { singular: lang?.month, plural: lang?.months, seconds: 2592000 },
    { singular: lang?.day, plural: lang?.days, seconds: 86400 },
    { singular: lang?.hour, plural: lang?.hours, seconds: 3600 },
    { singular: lang?.minute, plural: lang?.minutes, seconds: 60 },
  ];

  for (let i = 0; i < intervals.length; i++) {
    const interval = intervals[i];
    const intervalCount = Math.floor(timestampDifference / interval.seconds);

    if (intervalCount >= 1) {
      const label = intervalCount === 1 ? interval.singular : interval.plural;

      return navigator.language.startsWith("en")
        ? `${intervalCount} ${label} ${lang?.ago || "ago"}`
        : `${lang?.ago || "hace"} ${intervalCount} ${label}`;
    }
  }

  return `${lang?.just_now || "Just now"}`;
}

const getUserSpecialty = (user) => {
  if (!user) return "";
  if (user?.specializations && user.specializations.length > 0 && user.specializations[0]?.name) {
    return user.specializations[0].name;
  }
  if (user?.subcategory) {
    return user.subcategory.split(",")[0]?.trim();
  }
  if (user?.category) {
    return user.category;
  }
  if (user?.mentor_profile?.specialty) {
    return user.mentor_profile.specialty;
  }
  return "";
};

const formatActorName = (name, title) => {
  if (!name) return "";
  const cap = capitalizeWords(name);
  if (cap.toLowerCase().startsWith("dr")) return cap;
  const t = (title || "").toLowerCase();
  if (t === "doctor" || t === "dr." || t === "dr") {
    return `Dr. ${cap}`;
  }
  return cap;
};

const NotificationCardFollow = ({ value, currentNotificationType }) => {
  const { lang } = useContext(Language);
  const Navigate = useNavigate();

  const handleNavigation = (path) => {
    sessionStorage.setItem("lastNotificationType", currentNotificationType);
    sessionStorage.setItem("returnFromNotificationNavigation", "true");
    Navigate(path);
  };

  const hasMultipleUsers = value.users_count > 1;
  const primaryUser = value.users?.[0];
  const specialty = getUserSpecialty(primaryUser);
  const isUnread = value.data.is_read === 0;

  return (
    <div
      className={`notif-item-card ${isUnread ? "is-unread" : ""}`}
      onClick={() => handleNavigation(PROFILE + "/" + value.users[0]?.id)}
      key={value.id}
    >
      {/* Left Identity: Avatar + Action Badge */}
      <div className="notif-item-identity">
        {hasMultipleUsers ? (
          <div className="notif-multi-avatar-wrap">
            <div className="notif-stack-front">
              <ImageComponent
                src={
                  value?.users[0]?.thumbnail
                    ? _imagesURL + value.users[0].thumbnail
                    : Avatar
                }
                compClassName="w-100 h-100 object-fit-cover"
                loader_height="100%"
                loader_width="100%"
                fallbackSrc={Place}
              />
            </div>
            <div className="notif-stack-back">
              <ImageComponent
                src={
                  value?.users[1]?.thumbnail
                    ? _imagesURL + value.users[1].thumbnail
                    : Avatar
                }
                compClassName="w-100 h-100 object-fit-cover"
                loader_height="100%"
                loader_width="100%"
                fallbackSrc={Place}
              />
            </div>
            {renderActionBadge(value.data.type, value.data.notification_type)}
          </div>
        ) : (
          <div className="notif-avatar-badge-wrap">
            <div className="notif-user-avatar-img">
              <ImageComponent
                src={
                  value?.users[0]?.thumbnail
                    ? _imagesURL + value.users[0].thumbnail
                    : Avatar
                }
                compClassName="w-100 h-100 object-fit-cover"
                loader_height="100%"
                loader_width="100%"
                fallbackSrc={Place}
              />
            </div>
            {renderActionBadge(value.data.type, value.data.notification_type)}
          </div>
        )}
      </div>

      {/* Center: Structured Text Hierarchy with Medical Polish */}
      <div className="notif-item-body">
        <div className="notif-item-actor-row">
          {hasMultipleUsers ? (
            <span className="notif-item-actors">
              <span className="notif-actor-name">
                {formatActorName(value?.users[0]?.name, value?.users[0]?.user_title)}
              </span>
              {value?.users[0]?.verified === 1 && (
                <BsPatchCheckFill
                  className="notif-verified-badge"
                  size={13}
                  color="#00BADA"
                  title={lang?.verified_submission || "Verified Doctor"}
                />
              )}
              {" "}{lang?.and || "and"}{" "}
              <span className="notif-actor-name">
                {value.users_count > 2 ? (lang?.others || "others") : formatActorName(value?.users[1]?.name, value?.users[1]?.user_title)}
              </span>
            </span>
          ) : (
            <div className="notif-actor-name-wrap">
              <span className="notif-actor-name">
                {formatActorName(value?.users[0]?.name, value?.users[0]?.user_title)}
              </span>
              {primaryUser?.verified === 1 && (
                <BsPatchCheckFill
                  className="notif-verified-badge"
                  size={13}
                  color="#00BADA"
                  title={lang?.verified_submission || "Verified Doctor"}
                />
              )}
              {specialty && (
                <span className="notif-actor-specialty" title={specialty}>
                  <IoEyeOutline size={11} className="notif-specialty-eye-icon" />
                  {specialty}
                </span>
              )}
            </div>
          )}
        </div>

        <p className="notif-item-action-line">
          <GetNotificationBody
            notificationType={value.data.notification_type}
            type={value.data.type}
            fallbackBody={value.data.body}
            isSingle={!hasMultipleUsers}
          />
        </p>

        <div className="notif-item-time-row">
          <BsClock size={11} className="notif-time-icon" />
          <span className="notif-item-time">
            <UnixToRelative unixTimestamp={value.data.time} />
          </span>
          {isUnread && (
            <span className="notif-unread-pill">{lang?.new_badge || "New"}</span>
          )}
        </div>
      </div>

      {/* Right Trailing Action Area */}
      <div className="notif-item-right">
        <div className="notif-chevron-wrap">
          <FiChevronRight className="notif-chevron-arrow" />
        </div>
      </div>
    </div>
  );
};

const NotificationCardLike = ({ value, currentNotificationType }) => {
  const { lang } = useContext(Language);
  const Navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  const handleNavigation = (path) => {
    sessionStorage.setItem("lastNotificationType", currentNotificationType);
    sessionStorage.setItem("returnFromNotificationNavigation", "true");
    Navigate(path);
  };

  const handleProfileNavigation = (userId, e) => {
    if (e) e.stopPropagation();
    handleNavigation(PROFILE + "/" + userId);
  };

  const handleMediaNavigation = (e) => {
    if (e) e.stopPropagation();
    let path;
    const itemType = (value.data.type || "").toLowerCase();
    const postType = (value.data.post_type || "").toLowerCase();

    if (itemType === "event" || postType === "event") {
      path = EVENT_DETAILS + value.data.post_id;
    } else if (itemType === "webinar" || postType === "webinar") {
      path = WEBINAR_PAGE_DETAILS + value.data.post_id;
    } else if (
      itemType === "clinical-session" ||
      itemType === "clinical_session" ||
      itemType === "clinicalsession" ||
      postType === "clinical-session" ||
      postType === "clinical_session" ||
      postType === "clinicalsession"
    ) {
      path = SESSION_PAGE_DETAILS + value.data.post_id;
    } else if (itemType === "course" || postType === "course") {
      sessionStorage.setItem("CourseId", value.data.post_id);
      path = NEW_COURSE_DETAILS + value.data.post_id;
    } else {
      path = POST_DETAILS + value.data.post_id;
    }
    handleNavigation(path);
  };

  const handleRowClick = () => {
    if (value.data.post_id) {
      handleMediaNavigation();
    } else if (value.users && value.users[0]?.id) {
      handleProfileNavigation(value.users[0].id);
    }
  };

  const hasMultipleUsers = value.users_count > 1;
  const primaryUser = value.users?.[0];
  const specialty = getUserSpecialty(primaryUser);
  const isUnread = value.data.is_read === 0;
  const isPoll = value.data.post_type === "poll";

  return (
    <>
      <div
        className={`notif-item-card ${isUnread ? "is-unread" : ""}`}
        onClick={handleRowClick}
        key={value.id}
      >
        {/* Left Identity: Avatar + Action Type Badge */}
        <div className="notif-item-identity">
          {hasMultipleUsers ? (
            <div className="notif-multi-avatar-wrap">
              <div
                className="notif-stack-front"
                onClick={(e) => handleProfileNavigation(value.users[0].id, e)}
              >
                <ImageComponent
                  src={
                    value?.users[0]?.thumbnail
                      ? _imagesURL + value.users[0].thumbnail
                      : Avatar
                  }
                  compClassName="w-100 h-100 object-fit-cover"
                  loader_height="100%"
                  loader_width="100%"
                  fallbackSrc={Place}
                />
              </div>
              <div
                className="notif-stack-back"
                onClick={(e) => {
                  if (value.users_count > 2) {
                    e.stopPropagation();
                    setIsOpen(true);
                  } else {
                    handleProfileNavigation(value.users[1].id, e);
                  }
                }}
              >
                <ImageComponent
                  src={
                    value?.users[1]?.thumbnail
                      ? _imagesURL + value.users[1].thumbnail
                      : Avatar
                  }
                  compClassName="w-100 h-100 object-fit-cover"
                  loader_height="100%"
                  loader_width="100%"
                  fallbackSrc={Place}
                />
              </div>
              {renderActionBadge(value.data.type, value.data.notification_type)}
            </div>
          ) : (
            <div
              className="notif-avatar-badge-wrap"
              onClick={(e) => handleProfileNavigation(value.users[0]?.id, e)}
            >
              <div className="notif-user-avatar-img">
                <ImageComponent
                  src={
                    value?.users[0]?.thumbnail
                      ? _imagesURL + value.users[0].thumbnail
                      : Avatar
                  }
                  compClassName="w-100 h-100 object-fit-cover"
                  loader_height="100%"
                  loader_width="100%"
                  fallbackSrc={Place}
                />
              </div>
              {renderActionBadge(value.data.type, value.data.notification_type)}
            </div>
          )}
        </div>

        {/* Center: Structured Text Hierarchy with Medical Polish */}
        <div className="notif-item-body">
          <div className="notif-item-actor-row">
            {hasMultipleUsers ? (
              <span className="notif-item-actors">
                <span
                  className="notif-actor-name"
                  onClick={(e) => handleProfileNavigation(value.users[0].id, e)}
                >
                  {formatActorName(value?.users[0]?.name, value?.users[0]?.user_title)}
                </span>
                {value?.users[0]?.verified === 1 && (
                  <BsPatchCheckFill
                    className="notif-verified-badge"
                    size={13}
                    color="#00BADA"
                    title={lang?.verified_submission || "Verified Doctor"}
                  />
                )}
                {" "}{lang.and || "and"}{" "}
                <span
                  className="notif-actor-name"
                  onClick={(e) => {
                    if (value.users_count > 2) {
                      e.stopPropagation();
                      setIsOpen(true);
                    } else {
                      handleProfileNavigation(value.users[1].id, e);
                    }
                  }}
                >
                  {value.users_count > 2 ? (lang.others || "others") : formatActorName(value?.users[1]?.name, value?.users[1]?.user_title)}
                </span>
              </span>
            ) : (
              <div className="notif-actor-name-wrap">
                <span
                  className="notif-actor-name"
                  onClick={(e) => handleProfileNavigation(value.users[0]?.id, e)}
                >
                  {formatActorName(value?.users[0]?.name, value?.users[0]?.user_title)}
                </span>
                {primaryUser?.verified === 1 && (
                  <BsPatchCheckFill
                    className="notif-verified-badge"
                    size={13}
                    color="#00BADA"
                    title={lang?.verified_submission || "Verified Doctor"}
                  />
                )}
                {specialty && (
                  <span className="notif-actor-specialty" title={specialty}>
                    <IoEyeOutline size={11} className="notif-specialty-eye-icon" />
                    {specialty}
                  </span>
                )}
              </div>
            )}
          </div>

          <p className="notif-item-action-line">
            <GetNotificationBody
              notificationType={value.data.notification_type}
              type={value.data.type}
              fallbackBody={value.data.body}
              isSingle={!hasMultipleUsers}
            />
          </p>

          <div className="notif-item-time-row">
            <BsClock size={11} className="notif-time-icon" />
            <span className="notif-item-time">
              <UnixToRelative unixTimestamp={value.data.time} />
            </span>
            {isUnread && (
              <span className="notif-unread-pill">{lang?.new_badge || "New"}</span>
            )}
          </div>
        </div>

        {/* Right Action / Media Area */}
        <div className="notif-item-right">
          {value.data.media ? (
            <div
              className="notif-media-preview-box"
              onClick={handleMediaNavigation}
              title="View post"
            >
              <img
                src={_imagesURL + value.data.media}
                alt={lang?.image || "media"}
                onError={(e) => {
                  if (e.target.src !== Place) e.target.src = Place;
                }}
              />
            </div>
          ) : isPoll ? (
            <div
              className="notif-media-preview-box notif-poll-tile"
              onClick={handleMediaNavigation}
              title="View poll"
            >
              <LuBarChart2 size={18} color="#00BADA" />
              <span className="notif-poll-label">POLL</span>
            </div>
          ) : null}
          <div className="notif-chevron-wrap">
            <FiChevronRight className="notif-chevron-arrow" />
          </div>
        </div>
      </div>
      <PurchasedUsersModal isOpen={isOpen} toggle={() => setIsOpen(!isOpen)} courseId={value.data.post_id} />
    </>
  );
};

const MENTOR_NOTIF_TITLE_KEYS = {
  request: "mentorship_notif_title_request",
  schedule: "mentorship_notif_title_schedule",
  reminder: "mentorship_notif_title_reminder",
  start: "mentorship_notif_title_start",
  complete: "mentorship_notif_title_complete",
};

const formatNotificationTime = (unixSeconds, lang) => {
  const timestamp = Number(unixSeconds);
  if (!timestamp) return "";
  const diffSeconds = Math.max(Math.floor(Date.now() / 1000 - timestamp), 0);
  const ago = lang?.ago || "ago";
  const isEnglish = navigator.language.startsWith("en");

  if (diffSeconds < 60) return lang?.just_now || "Just now";

  const build = (count, singular, plural) => {
    const label = count === 1 ? singular : plural;
    return isEnglish ? `${count} ${label} ${ago}` : `${ago} ${count} ${label}`;
  };

  if (diffSeconds < 3600) {
    return build(Math.floor(diffSeconds / 60), lang?.min || "min", lang?.mins || "mins");
  }
  if (diffSeconds < 86400) {
    return build(Math.floor(diffSeconds / 3600), lang?.hr || "hr", lang?.hrs || "hrs");
  }
  if (diffSeconds < 604800) {
    return build(Math.floor(diffSeconds / 86400), lang?.day || "day", lang?.days || "days");
  }
  return build(Math.floor(diffSeconds / 604800), lang?.week || "week", lang?.weeks || "weeks");
};

const formatScheduleDateTime = (unixSeconds) => {
  const timestamp = Number(unixSeconds);
  if (!timestamp) return "";
  const date = new Date(timestamp * 1000);
  const datePart = date.toLocaleDateString(navigator.language, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const timePart = date.toLocaleTimeString(navigator.language, {
    hour: "numeric",
    minute: "2-digit",
  });
  const offsetMinutes = -date.getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? "+" : "-";
  const offsetHours = Math.floor(Math.abs(offsetMinutes) / 60);
  return `${datePart} at ${timePart} (GMT${sign}${offsetHours})`;
};

const formatTimeOnly = (unixSeconds) => {
  const timestamp = Number(unixSeconds);
  if (!timestamp) return "";
  return new Date(timestamp * 1000).toLocaleTimeString(navigator.language, {
    hour: "numeric",
    minute: "2-digit",
  });
};

const NotificationCardMentor = ({ value, currentNotificationType }) => {
  const { lang } = useContext(Language);
  const Navigate = useNavigate();

  const notificationType = value.data.notification_type || "";
  const isMentee = notificationType.endsWith("_as_mentee");
  const baseType = isMentee
    ? notificationType.replace("_as_mentee", "")
    : notificationType;
  const user = value?.users?.[0];

  const roleLabel = isMentee
    ? lang?.mentorship_as_mentee_label || "As Mentee"
    : lang?.mentorship_as_mentor_label || "As Mentor";
  const RoleIcon = isMentee ? IoPersonOutline : LuStethoscope;

  const isSchedule = baseType === "schedule";
  const isStart = baseType === "start";
  const isComplete = baseType === "complete";
  const isRequest = baseType === "request";
  const isReminder = baseType === "reminder";
  const isApproved = baseType === "approved";

  const title = isApproved
    ? lang?.mentorship_notif_approved_title || "You're a Mentor now! 🎉"
    : isSchedule && isMentee
      ? lang?.mentorship_notif_title_schedule_as_mentee || "Request Accepted"
      : isRequest && isMentee
        ? lang?.mentorship_notif_title_request_as_mentee || "Request Submitted"
        : lang?.[MENTOR_NOTIF_TITLE_KEYS[baseType]] ||
          lang?.mentorship_notif_title_generic ||
          "Mentorship Update";

  const requestUsers = value?.users || [];
  const renderRequestUsers = () => (
    <span className="notif-user-highlight" style={{ color: "#00BADA" }}>
      {capitalizeWords(requestUsers[0]?.name)}
    </span>
  );

  const sessionStartTimestamp = value.data.mentor_request?.start_timestamp || value.data.time;
  const scheduleDateTime = isSchedule
    ? formatScheduleDateTime(sessionStartTimestamp)
    : "";

  const handleNavigation = () => {
    sessionStorage.setItem("lastNotificationType", currentNotificationType);
    sessionStorage.setItem("returnFromNotificationNavigation", "true");
    Navigate(MENTORSHIP);
  };

  const isUnread = value.data.is_read === 0;

  return (
    <div
      className={`notif-item-card notif-mentor-card ${isUnread ? "is-unread" : ""} ${isReminder ? "reminder-accent" : ""} ${isStart ? "start-accent" : ""}`}
      onClick={handleNavigation}
      key={value.id}
    >
      {/* Left: Identity with overlapping badge */}
      <div className="notif-item-identity">
        <div className="notif-avatar-badge-wrap">
          <div className="notif-user-avatar-img">
            {user?.image && !(isSchedule && !isMentee) ? (
              <ImageComponent
                src={_imagesURL + user.image}
                compClassName="w-100 h-100 object-fit-cover"
                loader_height="100%"
                loader_width="100%"
                fallbackSrc={Place}
              />
            ) : (
              <div
                className="notif-mentor-fallback-avatar"
                style={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: isReminder
                    ? "rgba(124, 92, 252, 0.12)"
                    : isApproved || (isRequest && isMentee) || isStart
                      ? "rgba(0, 186, 218, 0.12)"
                      : "rgba(21, 128, 61, 0.12)",
                }}
              >
                {isReminder ? (
                  <BsClock size={19} color="#7C5CFC" />
                ) : isApproved ? (
                  <IoSchoolOutline size={19} color="#00BADA" />
                ) : isRequest && isMentee ? (
                  <BsCheckCircle size={19} color="#00BADA" />
                ) : isStart ? (
                  <GoDeviceCameraVideo size={19} color="#00BADA" />
                ) : (
                  <LuCalendarDays size={19} color="#15803D" />
                )}
              </div>
            )}
          </div>
          <span
            className="notif-action-badge"
            style={{
              background: isReminder
                ? "linear-gradient(135deg, #A78BFA 0%, #7C5CFC 100%)"
                : isApproved || isStart
                  ? "linear-gradient(135deg, #38BDF8 0%, #00BADA 100%)"
                  : "linear-gradient(135deg, #34D399 0%, #10B981 100%)",
            }}
          >
            <LuGraduationCap size={10} color="#fff" />
          </span>
        </div>
      </div>

      {/* Center: Structured Text Hierarchy */}
      <div className="notif-item-body">
        <div className="notif-mentor-top-row">
          <span className="notif-actor-name">{title}</span>
          {!isApproved && (
            <span
              className="notif-mentor-role-chip"
              style={{
                color: isReminder || (isStart && !isMentee) ? "#fff" : "#061223",
                background: isReminder
                  ? "#7E57C2"
                  : isStart && !isMentee
                    ? "#00BADA"
                    : isMentee
                      ? "#F1F5F9"
                      : "#E6F9FC",
              }}
            >
              {isReminder ? <FaClock size={10} /> : <RoleIcon size={10} />}{" "}
              {isReminder
                ? lang?.mentorship_notif_reminder_badge || "15 Minutes Left"
                : roleLabel}
            </span>
          )}
        </div>

        <p className="notif-item-action-line notif-mentor-action-text">
          {isApproved ? (
            <>
              {lang?.mentorship_notif_approved_prefix ||
                "Congratulations! Your application has been"}{" "}
              <span className="notif-mentor-cyan-highlight">
                {lang?.mentorship_notif_approved_highlight || "approved"}
              </span>
              {". "}
              {lang?.mentorship_notif_approved_suffix ||
                "You can now start accepting mentees and scheduling sessions."}
            </>
          ) : isSchedule ? (
            <>
              {isMentee ? (
                <>
                  <span className="notif-mentor-cyan-highlight">
                    {capitalizeWords(user?.name)}
                  </span>{" "}
                  {lang?.mentorship_notif_schedule_as_mentee_text ||
                    "accepted your mentorship request and confirmed your session"}{" "}
                  {scheduleDateTime}
                </>
              ) : (
                <>
                  {lang?.mentorship_notif_schedule_prefix ||
                    "You confirmed a session with"}{" "}
                  <span className="notif-mentor-cyan-highlight">
                    {capitalizeWords(user?.name)}
                  </span>
                  {" • "}
                  {scheduleDateTime}
                </>
              )}
            </>
          ) : isStart ? (
            <>
              {lang?.mentorship_notif_start_prefix || "Your session with"}{" "}
              <span className="notif-mentor-cyan-highlight">
                {capitalizeWords(user?.name)}
              </span>{" "}
              {lang?.mentorship_notif_start_suffix || "has started. Join now."}
            </>
          ) : isComplete ? (
            <>
              {lang?.mentorship_notif_complete_prefix ||
                "Your mentorship session with"}{" "}
              <span className="notif-mentor-cyan-highlight">
                {capitalizeWords(user?.name)}
              </span>{" "}
              {isMentee
                ? lang?.mentorship_notif_complete_as_mentee_suffix ||
                  "has been completed. We hope it was helpful!"
                : lang?.mentorship_notif_complete_suffix ||
                  "has been completed."}
            </>
          ) : isRequest ? (
            <>
              {isMentee ? (
                <>
                  {lang?.mentorship_notif_request_as_mentee_prefix ||
                    "Your mentorship request to"}{" "}
                  {renderRequestUsers()}{" "}
                  {lang?.mentorship_notif_request_as_mentee_suffix ||
                    "has been submitted successfully."}
                </>
              ) : (
                <>
                  {renderRequestUsers()}{" "}
                  {lang?.mentorship_notif_request_suffix_single ||
                    "has sent you a mentorship request and is awaiting your response."}
                </>
              )}
            </>
          ) : isReminder ? (
            <>
              {lang?.mentorship_notif_reminder_prefix || "Your session with"}{" "}
              <span className="notif-mentor-cyan-highlight">
                {capitalizeWords(user?.name)}
              </span>{" "}
              {lang?.mentorship_notif_reminder_middle ||
                "is scheduled for today at"}{" "}
              {formatTimeOnly(sessionStartTimestamp)}
              {". "}
              {lang?.mentorship_notif_reminder_suffix || "Get ready to join."}
            </>
          ) : (
            value.data.body && <span>{value.data.body}</span>
          )}
        </p>

        <div className="notif-item-time-row">
          <BsClock size={11} className="notif-time-icon" />
          <span className="notif-item-time">
            {formatNotificationTime(value.data.time, lang)}
          </span>
          {isUnread && (
            <span className="notif-unread-pill">{lang?.new_badge || "New"}</span>
          )}
        </div>
      </div>

      {/* Trailing Interaction */}
      <div className="notif-item-right">
        <div className="notif-chevron-wrap">
          <FiChevronRight className="notif-chevron-arrow" />
        </div>
      </div>
    </div>
  );
};

const NotificationCardMentorSkeleton = () => (
  <div className="notif-item-card notif-card-skeleton">
    <div className="notif-item-identity">
      <div className="skeleton-wave" style={{ width: "48px", height: "48px", borderRadius: "50%", flexShrink: 0 }} />
    </div>
    <div className="notif-item-body">
      <div className="skeleton-wave" style={{ width: "140px", height: "14px", borderRadius: "4px", marginBottom: "6px" }} />
      <div className="skeleton-wave" style={{ width: "75%", height: "12px", borderRadius: "4px", marginBottom: "6px" }} />
      <div className="skeleton-wave" style={{ width: "70px", height: "10px", borderRadius: "4px" }} />
    </div>
    <div className="notif-item-right">
      <div className="skeleton-wave" style={{ width: "26px", height: "26px", borderRadius: "50%", flexShrink: 0 }} />
    </div>
  </div>
);